import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullscreenIcon, FullscreenExitIcon, MicIcon, PauseIcon, PlayIcon, VolumeIcon, VolumeOffIcon } from '../Brand/Icon';
import LoadFailure from '../Brand/LoadFailure';
import CompactLoaderMark from '../Brand/CompactLoaderMark';
import useSinglePlayer, { isInteractiveCard } from '../ExternalLink/playerState';
import MediaViewportContext, { revealPlayer } from '../ExternalLink/MediaViewportContext';
import { requestMediaPlayback } from '../../media/client';
import { pollMediaCapabilities } from '../../media/capabilityPolling';
import useVideoFullscreen from './useVideoFullscreen';
import AudioWaveform from './AudioWaveform';
import './index.scss';

const stop = event => event.stopPropagation();
const pendingStates = new Set(['uploading', 'queued', 'processing']);
const failedStates = new Set(['failed', 'rejected', 'cancelled']);
const finiteDuration = value => Number.isFinite(value) && value > 0 ? value : 0;
export function formatMediaTime(seconds) {
  const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
}

function Player({ media, localUrl, reactionId, inline = false, status: inlineStatus, processing = false }) {
  const { t, i18n } = useTranslation('media');
  const text = (key, fallback, options = {}) => t(key, { defaultValue: fallback, ...options });
  const video = media.kind === 'video';
  const Container = inline ? 'span' : 'div';
  const root = useRef(null);
  const element = useRef(null);
  const { fullscreen, toggleFullscreen, fullscreenError, nativeFullscreen } = useVideoFullscreen(root, element, video);
  const controlsTimer = useRef(null);
  const [controlsAwake, setControlsAwake] = useState(false);
  const [controlsFocused, setControlsFocused] = useState(false);
  const mounted = useRef(true);
  const loading = useRef(null);
  const playbackRequest = useRef(null);
  const requestVersion = useRef(0);
  const playbackAttempt = useRef(0);
  const intent = useRef(false);
  const completed = useRef(false);
  const [source, setSource] = useState(localUrl || media.playback_url || '');
  const [poster, setPoster] = useState(media.poster_url || '');
  const [waveformUrl, setWaveformUrl] = useState(media.waveform_url || '');
  const [state, setState] = useState(source ? 'ready' : 'idle');
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(finiteDuration(Number(media.duration_ms) / 1000));
  const [speed, setSpeed] = useState(1);
  const language = i18n?.language || 'en';
  // Progress updates every animation frame; formatting the unchanged speed
  // only depends on the selected speed and language.
  const speedLabel = useMemo(() => `${new Intl.NumberFormat(language).format(speed)}×`, [language, speed]);
  const [disabled, setDisabled] = useState(false);
  const prepareViewport = useContext(MediaViewportContext);
  const { active, open, close } = useSinglePlayer(() => {
    intent.current = false;
    playbackAttempt.current += 1;
    element.current?.pause();
    if (mounted.current) setPlaying(false);
  });
  const pending = !localUrl && pendingStates.has(media.status);
  const failed = !localUrl && failedStates.has(media.status);
  const unavailable = !localUrl && (disabled || media.playback_available === false);
  const available = !pending && !failed && !unavailable;

  useEffect(() => {
    mounted.current = true;
    const node = element.current;
    return () => {
      mounted.current = false; requestVersion.current += 1; intent.current = false;
      playbackRequest.current?.abort();
      clearTimeout(controlsTimer.current);
      node?.pause();
    };
  }, []);

  const revealControls = () => {
    clearTimeout(controlsTimer.current);
    setControlsAwake(true);
    controlsTimer.current = setTimeout(() => setControlsAwake(false), 2500);
  };

  const syncPosition = useCallback(() => {
    const node = element.current;
    if (!node || !Number.isFinite(node.currentTime)) return;
    // The last timeupdate/frame can precede the actual end. Keep the endpoint
    // latched until the user seeks or replays, including late native events.
    setPosition(completed.current || node.ended
      ? finiteDuration(node.duration) || duration || node.currentTime
      : node.currentTime);
  }, [duration]);
  const syncDuration = () => {
    const next = finiteDuration(element.current?.duration);
    if (!next) return;
    setDuration(next);
    if (completed.current || element.current?.ended) setPosition(next);
  };

  useEffect(() => {
    if (!playing || typeof window.requestAnimationFrame !== 'function') return undefined;
    let frame;
    let cancelled = false;
    const tick = () => {
      if (cancelled || !mounted.current) return;
      syncPosition();
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => { cancelled = true; window.cancelAnimationFrame(frame); };
  }, [playing, syncPosition]);

  const load = useCallback(async () => {
    if (loading.current) return loading.current;
    if (localUrl || media.playback_url) {
      const url = localUrl || media.playback_url;
      setSource(url);
      setPoster(media.poster_url || '');
      setWaveformUrl(media.waveform_url || '');
      setState('ready');
      return url;
    }
    const version = requestVersion.current;
    const controller = new AbortController();
    playbackRequest.current = controller;
    setState('loading');
    const request = requestMediaPlayback(media.id, reactionId, controller.signal).then(result => {
      if (!result.playback_url) throw new Error('Playback unavailable');
      if (!mounted.current || requestVersion.current !== version) return '';
      setSource(result.playback_url);
      setPoster(result.poster_url || '');
      setWaveformUrl(result.waveform_url || '');
      setState('ready');
      return result.playback_url;
    }).catch(() => {
      if (mounted.current && requestVersion.current === version) setState('error');
      return '';
    }).finally(() => {
      if (loading.current === request) loading.current = null;
      if (playbackRequest.current === controller) playbackRequest.current = null;
    });
    loading.current = request;
    return request;
  }, [localUrl, media.id, media.playback_url, media.poster_url, media.waveform_url, reactionId]);

  useEffect(() => {
    if (source || !available || state !== 'idle') return undefined;
    // Acquire signed URLs/posters only for visible, current cards. Inactive
    // feed copies must not preload all recordings in a maximum-size opinion.
    let visible = !window.IntersectionObserver;
    const check = () => { if (visible && isInteractiveCard(root.current)) load(); };
    const intersection = window.IntersectionObserver && new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      check();
    });
    if (intersection && root.current) intersection.observe(root.current);
    else check();
    const observer = new MutationObserver(check);
    let parent = root.current?.parentElement;
    while (parent && parent !== document.body) {
      observer.observe(parent, { attributes: true, attributeFilter: ['class', 'hidden', 'aria-hidden', 'inert'] });
      parent = parent.parentElement;
    }
    return () => { intersection?.disconnect(); observer.disconnect(); };
  }, [source, available, state, load]);

  useEffect(() => {
    if (!active) return undefined;
    const check = () => { if (!isInteractiveCard(root.current)) close(); };
    check();
    const observer = new MutationObserver(check);
    let parent = root.current;
    while (parent && parent !== document.body) {
      observer.observe(parent, { attributes: true, attributeFilter: ['class', 'hidden', 'aria-hidden', 'inert', 'style'] });
      parent = parent.parentElement;
    }
    const intersection = !fullscreen && window.IntersectionObserver ? new IntersectionObserver(entries => {
      if (entries.some(entry => !entry.isIntersecting)) close();
    }) : null;
    if (intersection && root.current) intersection.observe(root.current);
    return () => { observer.disconnect(); intersection?.disconnect(); };
  }, [active, close, fullscreen]);

  useEffect(() => {
    if (!available) close();
  }, [available, close]);
  useEffect(() => {
    if (!active || localUrl) return undefined;
    // The API revokes every subsequent Range request immediately. Refresh the
    // switch while playing too, so already buffered playback stops on receipt.
    const controller = new AbortController();
    const refresh = pollMediaCapabilities(controller.signal);
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [active, localUrl]);
  useEffect(() => {
    const update = event => {
      if (localUrl) return;
      const stopped = event.detail?.playback_enabled === false;
      setDisabled(stopped);
      if (stopped) {
        requestVersion.current += 1;
        playbackRequest.current?.abort();
        playbackRequest.current = null;
        loading.current = null;
        close();
        // Drop the cached request on emergency shutdown; retry gets new rights.
        setSource('');
        setPoster('');
        setWaveformUrl('');
        setState('idle');
      }
    };
    window.addEventListener('wiw-media-capabilities', update);
    return () => window.removeEventListener('wiw-media-capabilities', update);
  }, [localUrl, close]);
  // Recorded video stays exactly where the viewer tapped. Only the explicit
  // reader control may expand its comment; audio keeps the existing policy.
  useLayoutEffect(() => { if (active && !video) revealPlayer(root.current); }, [active, video]);

  const toggle = async () => {
    if (!available || !isInteractiveCard(root.current)) return;
    if (playing || intent.current) { close(); return; }
    if (!video) prepareViewport?.();
    open();
    intent.current = true;
    const attempt = ++playbackAttempt.current;
    const url = source || await load();
    if (!mounted.current || playbackAttempt.current !== attempt || !intent.current) return;
    if (!url || !isInteractiveCard(root.current)) { close(); return; }
    const node = element.current;
    if (!node) { close(); return; }
    // React may not yet have committed the signed URL after the await.
    if (node.getAttribute('src') !== url) node.src = url;
    if (completed.current || node.ended) {
      completed.current = false;
      node.currentTime = 0;
      setPosition(0);
    }
    node.playbackRate = speed;
    node.preservesPitch = true;
    try { await node.play(); }
    catch (error) {
      // A cancelled play() may reject after the next tap has started a new
      // attempt. That old promise must not stop the new playback or show error.
      if (!mounted.current || playbackAttempt.current !== attempt || !intent.current) return;
      // A mobile browser can require a fresh tap after asynchronous URL
      // loading. Keep the play target usable instead of declaring the file
      // broken; the next tap calls play() directly with the prepared source.
      if (!['AbortError', 'NotAllowedError'].includes(error?.name)) setState('error');
      close();
    }
  };
  const toggleMuted = () => {
    const node = element.current;
    if (!node || !available || !isInteractiveCard(root.current)) return;
    const next = !muted;
    node.muted = next;
    if (!next && node.volume === 0) node.volume = 1;
    setMuted(next);
  };
  const retry = async () => {
    close();
    const version = requestVersion.current;
    const url = localUrl || media.playback_url || await load();
    if (!url || !mounted.current || requestVersion.current !== version) return;
    const node = element.current;
    if (!node) return;
    if (node.getAttribute('src') !== url) node.src = url;
    // The API can return the same signed URL. React then leaves src untouched,
    // so the native decoder's error must be reset explicitly on retry.
    node.load();
    completed.current = false;
    setPosition(0);
    setState('ready');
  };
  const changeSpeed = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(next);
    if (element.current) element.current.playbackRate = next;
  };
  const seek = event => {
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    completed.current = false;
    if (element.current) element.current.currentTime = next;
    setPosition(next);
  };
  const statusKey = unavailable ? 'unavailable' : failed ? 'failed' : pending ? 'processing' : state === 'error' ? 'loadError' : 'loading';
  const statusDefaults = { unavailable: 'Playback is temporarily unavailable', failed: 'Recording unavailable', processing: 'Processing recording', loadError: 'Could not play recording', loading: 'Loading recording' };
  const ready = available && state !== 'error' && state !== 'loading';
  const busy = !failed && !unavailable && (pending || state === 'loading');
  const label = text(video ? 'video' : 'audio', video ? 'Video recording' : 'Audio recording');
  const playbackRequested = playing || active;
  const playbackLabel = text(playbackRequested ? 'pause' : 'play', playbackRequested ? 'Pause recording' : 'Play recording');
  const playButton = <button type="button"
    className="recorded-media__play"
    disabled={!ready} aria-label={text(playing ? 'pause' : 'play', playing ? 'Pause recording' : 'Play recording')}
    onClick={toggle}>{playing ? <PauseIcon size={24} /> : <PlayIcon size={24} />}</button>;
  const status = <span className="recorded-media__status" role="status" aria-label={text(statusKey, statusDefaults[statusKey])}>
    {busy && <CompactLoaderMark />}
    <span title={text(statusKey, statusDefaults[statusKey])}>{text(statusKey, statusDefaults[statusKey])}</span>
  </span>;
  const controls = <span className="recorded-media__controls">
    {video ? <button type="button" className="recorded-media__mute" disabled={!ready}
      aria-label={text(muted ? 'unmuteVideo' : 'muteVideo', muted ? 'Unmute video' : 'Mute video')}
      onClick={toggleMuted}>{muted ? <VolumeOffIcon size={24} /> : <VolumeIcon size={24} />}</button> : playButton}
    {!video && inlineStatus !== undefined && <span className="recorded-media__inline-status">{inlineStatus}</span>}
    <span className="recorded-media__timeline">
      {video ? <input type="range" min="0" max={duration || 0} step="any" value={Math.min(position, duration || 0)}
        disabled={!ready || !source || !duration} onChange={seek}
        aria-label={text('seek', 'Recording position')} aria-valuetext={`${formatMediaTime(position)} / ${formatMediaTime(duration)}`} />
        : <AudioWaveform url={waveformUrl} blob={media.blob} available={available} duration={duration} position={position}
          disabled={!ready || !source || !duration} onChange={seek} label={text('seek', 'Recording position')}
          valueText={`${formatMediaTime(position)} / ${formatMediaTime(duration)}`} />}
      <span className="recorded-media__time" aria-hidden="true">{formatMediaTime(position)} / {formatMediaTime(duration)}</span>
    </span>
    {!video && <button type="button" className="recorded-media__speed" onClick={changeSpeed}
      aria-label={text('speed', 'Playback speed: {{speed}}', { speed: speedLabel })}>{speedLabel}</button>}
    {video && <button type="button" className="recorded-media__fullscreen" disabled={!ready || !source}
      aria-label={text(fullscreen ? 'exitFullscreen' : 'fullscreen', fullscreen ? 'Exit fullscreen' : 'Fullscreen video')}
      onClick={toggleFullscreen}>{fullscreen ? <FullscreenExitIcon size={24} /> : <FullscreenIcon size={24} />}</button>}
  </span>;
  const mediaProps = {
    ref: element, className: 'recorded-media__asset', src: source || undefined, preload: 'metadata',
    onLoadedMetadata: syncDuration,
    onLoadedData: () => { if (video) setFrameReady(true); },
    onEmptied: () => { if (video) setFrameReady(false); },
    onDurationChange: syncDuration,
    onTimeUpdate: syncPosition,
    onVolumeChange: () => { if (video) setMuted(Boolean(element.current?.muted || element.current?.volume === 0)); },
    onPlay: () => {
      if ((!intent.current && !nativeFullscreen.current) || !available || !isInteractiveCard(root.current)) { element.current?.pause(); return; }
      intent.current = true;
      clearTimeout(controlsTimer.current); setControlsAwake(false);
      open(); setPlaying(true); setStarted(true); setState('ready');
    },
    onPause: () => { setPlaying(false); close(); },
    onEnded: () => {
      completed.current = true;
      syncDuration();
      syncPosition();
      setPlaying(false);
      close();
    },
    onError: () => { if (source) { setState('error'); close(); } },
  };
  return <Container ref={root} className={`recorded-media recorded-media--${video ? 'video' : 'audio'}`}
    role="group" aria-label={label} data-kind={media.kind} data-media-id={media.id} data-local-preview={localUrl ? 'true' : undefined}
    data-state={!available ? statusKey : state} data-no-swipe={!video || fullscreen || undefined}
    onClick={stop} onDoubleClick={stop} onPointerDown={stop} onTouchStart={stop} onTouchEnd={stop}>
    {video ? <>
      <span className="recorded-media__video-stage" onPointerMove={event => { if (event.pointerType === 'mouse' && playing) revealControls(); }}>
        <video {...mediaProps} playsInline poster={poster || undefined} aria-label={label} tabIndex={-1} onClick={toggle} />
        <button type="button" className="recorded-media__video-toggle" disabled={!ready}
          aria-label={playbackLabel} onClick={toggle}>
          {ready && !processing && !started && !playbackRequested && <span className="recorded-media__play--central" aria-hidden="true"><PlayIcon size={12} /></span>}
        </button>
        {inlineStatus !== undefined && <span className="recorded-media__inline-status recorded-media__video-progress">{inlineStatus}</span>}
        {!ready && !(state === 'error' && available) && <span className="recorded-media__video-progress">{status}</span>}
        {ready && !inlineStatus && source && !poster && !frameReady && <span className="recorded-media__video-progress"><CompactLoaderMark /></span>}
        {state === 'error' && available ? <span className="recorded-media__video-failure"><LoadFailure onRetry={retry} /></span>
          : <span className="recorded-media__video-controls" data-no-swipe
            data-visible={!playing || controlsAwake || controlsFocused}
            onFocusCapture={() => setControlsFocused(true)}
            onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setControlsFocused(false); }}
            onPointerDown={revealControls}>{controls}</span>}
      </span>
      {fullscreenError && <span role="status" className="recorded-media__status">{text('fullscreenUnavailable', 'Fullscreen is unavailable on this device')}</span>}
    </> : <>
      <audio {...mediaProps} aria-label={label} />
      <span className="recorded-media__audio-frame" aria-hidden="true" />
      {state === 'error' && available ? <LoadFailure onRetry={retry} />
        : ready ? controls : <><MicIcon className="recorded-media__status-icon" size={24} />{status}</>}
    </>}
  </Container>;
}

export function RecordedMediaPlayer(props) {
  if (!props.media || !['audio', 'video'].includes(props.media.kind)) return null;
  // Accepting an upload assigns a server ID while this same local take may be
  // playing. Preserve its player, position and speed until the source changes.
  return <Player key={props.localUrl || props.media.id} {...props} />;
}

export default function RecordedMedia({ media, reactionId }) {
  if (!Array.isArray(media) || !media.length) return null;
  return <div className="recorded-media-list">{media.map(item =>
    <RecordedMediaPlayer key={item.id} media={item} reactionId={reactionId} />)}</div>;
}
